import { useEffect, useState } from "react";
import { Mail, X, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { Button, Input, Label, Textarea } from "./ui-kit";

// Email the filtered Team Daily Report (Excel) from the configured sender mailbox.
export function EmailReportModal({ filters, scopeLabel, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/email/config").then((r) => {
      setCfg(r.data);
      setRecipients((r.data.default_recipients || []).join(", "));
    }).catch(() => setCfg({ configured: false }));
  }, []);

  const send = async (e) => {
    e.preventDefault();
    const list = recipients.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (!list.length) return toast.error("Add at least one recipient");
    setBusy(true);
    try {
      const r = await api.post("/reviews/daily-reports/email", { ...filters, recipients: list, message });
      toast.success(`Report emailed to ${r.data.sent_to.join(", ")} (${r.data.rows} rows)`);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Email failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose} data-testid="email-report-modal">
      <form onSubmit={send} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Mail className="w-5 h-5" /> Email report</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[#F2EDE4]" data-testid="email-report-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="text-xs text-[#5C6B5E] mb-4">
          {scopeLabel} · {filters.from} to {filters.to}
          {cfg?.sender && <> · from <b>{cfg.sender}</b></>}
        </div>
        {cfg && !cfg.configured && (
          <div className="mb-3 text-sm rounded-lg border border-[#E07A5F]/40 bg-[#E07A5F]/10 text-[#8B3A1F] p-3" data-testid="email-not-configured">
            Email sending isn't configured on the server yet.
          </div>
        )}
        <div className="space-y-3">
          <div>
            <Label>To (comma separated)</Label>
            <Input value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="name@taraigold.com, other@…" data-testid="email-recipients" />
          </div>
          <div>
            <Label>Message (optional)</Label>
            <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Short note to include above the summary" data-testid="email-message" />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || (cfg && !cfg.configured)} data-testid="email-report-send">
            <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send with Excel"}
          </Button>
        </div>
      </form>
    </div>
  );
}
